import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet-async';
import Box from '@mui/material/Box';
import { List, ListItemButton, ListItemText, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import { Button, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import {
  useCreateMigrationRecord,
  useGetMigrationRecordsByTreeId,
} from '../../../../hooks';
import { useAuth } from '../../../../components';
import { Person } from '../../../../types';
import queryClient from '../../../../core/http/react-query';
import { useModal } from '../../../../components';
import { AppConfig } from '../../../../core';
import ErrorDisplay from '../../../../components/common/ErrorDisplay';
import { maxMigrationRecords } from '../../../../core';
import CreateMigrationRecord from './CreateMigrationRecord';
import RecordDetails from './RecordDetails';
import useIsAdmin from '../../../../hooks/useIsAdmin';
import useSubscription from '../../../../hooks/useSubscription';
import SubscribeModal from '../../../../components/common/SubscribeModal';
import { truncateText } from '../../../../utils';

type MigrationProps = {
  treeId: string;
  treeMembers?: Person[];
  autoOpenCreate?: boolean;
  isActive?: boolean;
  preselectedRecordId?: string;
};

export default function MigrationTracking({
  treeId,
  treeMembers = [],
  autoOpenCreate = false,
  isActive = true,
  preselectedRecordId,
}: MigrationProps) {
  const { palette } = useTheme();
  const { currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { openModal, closeModal } = useModal();
  const userId = currentUser?.uid;

  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [selectedRecordId, setSelectedRecordId] = useState<string>('');
  const hasAutoOpenedRef = useRef(false);

  const { isAdmin } = useIsAdmin(currentUser);
  const { isPremium } = useSubscription();

  const isPaidUser = isPremium || isAdmin;

  const { data, isLoading, isError, error, refetch } =
    useGetMigrationRecordsByTreeId(treeId ?? '');

  const migrationRecords = useMemo(() => {
    if (data) {
      return data as any;
    }
    return null;
  }, [data]);

  const createMutation = useCreateMigrationRecord();

  const uniqueRecords = useMemo(() => {
    const mergedRecords = [...(migrationRecords ?? [])];
    return Array.from(
      new Map(mergedRecords.map((record: any) => [record.id, record])).values(),
    );
  }, [migrationRecords]);

  const handleListItemClick = (
    _event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    index: number,
    recordId: string,
  ) => {
    setSelectedIndex(index);
    setSelectedRecordId(recordId);
  };

  const handleAddRecord = useCallback(
    (payload: any) => {
      createMutation.mutate(payload, {
        onSuccess: () => {
          enqueueSnackbar('Migration record added successfully', {
            variant: 'success',
          });
          queryClient.refetchQueries({
            queryKey: ['migrationRecords', treeId],
            exact: true,
          });
          closeModal?.();
        },
        onError: (error) => {
          console.error('Error creating migration record:', error);
          enqueueSnackbar(`Error creating migration record: ${error.message}`, {
            variant: 'error',
          });
        },
      });
    },
    [closeModal, createMutation, enqueueSnackbar, treeId],
  );

  const isMigrationLimitReached = uniqueRecords.length >= maxMigrationRecords;

  useEffect(() => {
    if (!uniqueRecords.length) {
      if (selectedRecordId) {
        setSelectedIndex(0);
        setSelectedRecordId('');
      }
      return;
    }

    if (!selectedRecordId) {
      setSelectedIndex(0);
      setSelectedRecordId(uniqueRecords[0].id);
      return;
    }

    const selectedIndexFromList = uniqueRecords.findIndex(
      (record: any) => record.id === selectedRecordId,
    );
    if (selectedIndexFromList === -1) {
      setSelectedIndex(0);
      setSelectedRecordId(uniqueRecords[0].id);
    } else if (selectedIndexFromList !== selectedIndex) {
      setSelectedIndex(selectedIndexFromList);
    }
  }, [uniqueRecords, selectedRecordId, selectedIndex]);

  useEffect(() => {
    if (!preselectedRecordId || !uniqueRecords.length) {
      return;
    }

    const index = uniqueRecords.findIndex(
      (record: any) => record.id === preselectedRecordId,
    );
    if (index >= 0) {
      setSelectedIndex(index);
      setSelectedRecordId(preselectedRecordId);
    }
  }, [uniqueRecords, preselectedRecordId]);

  const openCreateJourneyModal = useCallback(() => {
    if (isMigrationLimitReached && !isPaidUser) {
      return openModal(
        <SubscribeModal
          closeModal={closeModal}
          handleClose={closeModal}
          message={`You can only add up to ${maxMigrationRecords} migration records.`}
          title="Add More Family Journeys"
        />,
      );
    }

    return openModal(
      <CreateMigrationRecord
        closeModal={closeModal}
        onAddRecord={handleAddRecord}
        treeId={treeId}
        userId={userId}
        treeMembers={treeMembers}
      />,
    );
  }, [
    closeModal,
    handleAddRecord,
    isMigrationLimitReached,
    isPaidUser,
    openModal,
    treeId,
    treeMembers,
    userId,
  ]);

  useEffect(() => {
    if (!isActive) {
      closeModal?.();
      hasAutoOpenedRef.current = false;
    }
  }, [isActive, closeModal]);

  useEffect(() => {
    if (!isActive || !autoOpenCreate || hasAutoOpenedRef.current || isLoading) {
      return;
    }

    hasAutoOpenedRef.current = true;
    openCreateJourneyModal();
  }, [
    autoOpenCreate,
    isActive,
    isLoading,
    isMigrationLimitReached,
    isPaidUser,
    openModal,
    closeModal,
    treeId,
    userId,
    openCreateJourneyModal,
  ]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100dvh',
          mt: '64px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return <ErrorDisplay error={error} onRetry={refetch} />;
  }

  return (
    <Box component="section" sx={{ flexGrow: 1, width: '100%', my: 1, pb: 2 }}>
      <Helmet>
        <title>Family Journeys | {AppConfig.appName}</title>
        <meta name="description" content="View your migration history" />
      </Helmet>
      <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            py: 1,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight="700">
              Family Journeys
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Keep your family migration path alive across generations.
            </Typography>
          </Box>
          {isMigrationLimitReached && !isPaidUser ? (
            <Box sx={{ textAlign: 'left' }}>
              <Tooltip
                title={`You can only add up to ${maxMigrationRecords} migration records.`}
              >
                <span>
                  <Button
                    size="medium"
                    disabled
                    sx={{
                      minHeight: 40,
                      px: 2,
                      borderRadius: 2,
                      textTransform: 'none',
                      alignSelf: { xs: 'stretch', sm: 'flex-start' },
                    }}
                  >
                    Add your journey
                  </Button>
                </span>
              </Tooltip>
            </Box>
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="medium"
              sx={{
                alignSelf: { xs: 'stretch', sm: 'flex-start' },
                borderRadius: 2,
                minHeight: 40,
                px: 2,
                boxShadow: 2,
                textTransform: 'none',
                transition:
                  'transform 220ms cubic-bezier(0.2, 0, 0, 1), box-shadow 220ms cubic-bezier(0.2, 0, 0, 1)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: 4,
                },
              }}
              startIcon={<TravelExploreIcon />}
              onClick={openCreateJourneyModal}
              disableElevation
            >
              Add journey
            </Button>
          )}
        </Box>
        <Box
          my={3}
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            lineHeight: '1.8',
            p: 3,
            border: `1px solid ${palette.divider}`,
            boxShadow: '0px 6px 20px rgba(16, 24, 40, 0.06)',
            animation: 'journeyIntroFade 220ms cubic-bezier(0.2, 0, 0, 1)',
            '@keyframes journeyIntroFade': {
              '0%': { opacity: 0, transform: 'translateY(6px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <Typography fontSize={'1.1rem'} lineHeight={1.6} color="text.primary">
            Your family&apos;s journey is part of your history. Capture your
            family&apos;s migration story, from ancestral roots to present-day
            locations, and pass it down through generations. Each journey
            reflects the strength, hope, and experiences that have shaped your
            family&apos;s identity. By documenting these moments, you create a
            lasting record that connects the past to the present and keeps your
            heritage alive for the future.
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          minHeight: { xs: 'auto', md: '62vh' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', md: '30%' },
            borderRadius: 3,
            bgcolor: 'primary.dark',
            border: `1px solid ${palette.primary.main}`,
            p: 2,
            color: 'primary.contrastText',
            boxShadow: '0px 8px 24px rgba(16, 24, 40, 0.08)',
          }}
        >
          <Box mb={2}>
            <Typography variant="h6" fontWeight="600">
              Your journeys
            </Typography>
          </Box>
          <Typography variant="body2">
            Select a record from the list to view its details.
          </Typography>
          <List component="nav" aria-label="migration-records">
            {!uniqueRecords?.length && (
              <Typography
                variant="body2"
                color="primary.contrastText"
                sx={{ mt: 1 }}
              >
                No journeys yet. Click Add journey to create the first one.
              </Typography>
            )}
            {uniqueRecords?.map((record: any, index: number) => (
              <ListItemButton
                key={record.id}
                selected={selectedIndex === index}
                onClick={(event) =>
                  handleListItemClick(event, index, record.id)
                }
                sx={{
                  bgcolor:
                    selectedIndex === index
                      ? 'primary.light'
                      : 'rgba(255, 255, 255, 0.06)',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor:
                    selectedIndex === index
                      ? 'rgba(255, 255, 255, 0.55)'
                      : 'transparent',
                  color: 'primary.contrastText',
                  mb: 1,
                  transition:
                    'transform 220ms cubic-bezier(0.2, 0, 0, 1), box-shadow 220ms cubic-bezier(0.2, 0, 0, 1), border-color 220ms cubic-bezier(0.2, 0, 0, 1)',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0px 6px 16px rgba(16, 24, 40, 0.22)',
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Typography
                      noWrap
                      fontWeight={selectedIndex === index ? 600 : 500}
                    >
                      {truncateText(record.title, 40)}
                    </Typography>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
        <Box
          sx={{
            width: { xs: '100%', md: '70%' },
            bgcolor: 'background.paper',
            borderRadius: 3,
            border: `1px solid ${palette.divider}`,
            boxShadow: '0px 10px 26px rgba(16, 24, 40, 0.08)',
            animation: 'journeyDetailsFade 220ms cubic-bezier(0.2, 0, 0, 1)',
            '@keyframes journeyDetailsFade': {
              '0%': { opacity: 0, transform: 'translateY(6px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
            p: 3,
          }}
        >
          {selectedRecordId ? (
            <RecordDetails
              recordId={selectedRecordId}
              treeId={treeId}
              onDelete={() => setSelectedRecordId('')}
            />
          ) : (
            <Box p={2} sx={{ minHeight: { xs: 240, md: 420 } }}>
              <Typography variant="h6">
                Add your family journey to see details here.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
